import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Z_ANNOTATOR, BRAND_CORAL, INK_DEEP } from "./qaStyles";
import { acquireQaFocus, releaseQaFocus } from "../../utils/qa/qaFocusBridge";

// Full-screen annotation editor: freehand pen + text labels, 6-color palette.
// The brand color clashes on brand-colored UI, so alternatives are mandatory; all
// swatches stay inside the design system's existing colors. Save bakes the ops into
// the JPEG and replaces the shot.
const COLORS = [BRAND_CORAL, "#608DF1", "#5EC37F", "#FFC564", "#FFFFFF", INK_DEEP];

const luminance = (hex) => {
  const n = parseInt(hex.slice(1), 16);
  return 0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255);
};
const haloFor = (color) => (luminance(color) > 150 ? INK_DEEP : "#FFFFFF");

const ui = {
  backdrop: {
    position: "fixed", inset: 0, zIndex: Z_ANNOTATOR, background: "rgba(30,41,57,0.88)",
    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px",
  },
  toolbar: {
    display: "flex", alignItems: "center", gap: "10px", background: "#FFFFFF",
    borderRadius: "8px", padding: "8px 12px", boxShadow: "0px 4px 14px rgba(24,24,28,0.25)",
    fontFamily: "Inter, sans-serif", flexWrap: "wrap", justifyContent: "center",
  },
  toolBtn: (active) => ({
    border: `1px solid ${active ? BRAND_CORAL : "#E6E6E6"}`, background: active ? "#FFF2F2" : "#FFFFFF",
    color: active ? BRAND_CORAL : "#5A5C60", borderRadius: "6px", padding: "6px 12px",
    fontSize: "12px", fontWeight: 600, cursor: "pointer", fontFamily: "Inter, sans-serif",
  }),
  swatch: (color, active) => ({
    width: "22px", height: "22px", borderRadius: "11px", background: color, cursor: "pointer",
    border: active ? `3px solid ${INK_DEEP}` : "1px solid #E6E6E6", boxSizing: "border-box", padding: 0,
  }),
  canvasWrap: { maxWidth: "92vw", maxHeight: "74vh", display: "flex", alignItems: "center", justifyContent: "center" },
};

const AnnotateShot = ({ shot, onSave, onCancel }) => {
  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const strokeRef = useRef(null);
  const backdropDownRef = useRef(false);
  const [tool, setTool] = useState("draw");
  const [color, setColor] = useState(COLORS[0]);
  const [ops, setOps] = useState([]);
  const [imageReady, setImageReady] = useState(false);
  const [textBox, setTextBox] = useState(null); // {x, y, clientX, clientY, value}

  // Relax any underlying MUI modal's focus trap while the annotator is open, so the
  // text-label input can hold focus (e.g. annotating over the Create Campaign modal).
  useEffect(() => {
    acquireQaFocus();
    return () => releaseQaFocus();
  }, []);

  const metrics = useMemo(() => {
    const naturalWidth = imageRef.current?.naturalWidth || 1;
    return {
      strokeWidth: Math.max(3, Math.round(naturalWidth / 400)),
      fontPx: Math.max(18, Math.round(naturalWidth / 55)),
    };
  }, [imageReady]);

  useEffect(() => {
    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      const canvas = canvasRef.current;
      if (canvas) {
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
      }
      setImageReady(true);
    };
    img.src = shot;
  }, [shot]);

  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img) return;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0);
    for (const op of ops) {
      if (op.type === "stroke") {
        ctx.strokeStyle = op.color;
        ctx.lineWidth = metrics.strokeWidth;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.beginPath();
        op.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
        ctx.stroke();
      } else if (op.type === "text") {
        ctx.font = `600 ${metrics.fontPx}px Inter, sans-serif`;
        ctx.textBaseline = "top";
        ctx.lineJoin = "round";
        ctx.lineWidth = Math.max(3, Math.round(metrics.fontPx / 6));
        ctx.strokeStyle = haloFor(op.color);
        ctx.strokeText(op.value, op.x, op.y);
        ctx.fillStyle = op.color;
        ctx.fillText(op.value, op.x, op.y);
      }
    }
  }, [ops, metrics, imageReady]);

  useEffect(() => {
    redraw();
  }, [redraw]);

  const toCanvasCoords = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * canvas.width,
      y: ((event.clientY - rect.top) / rect.height) * canvas.height,
      scale: rect.width / canvas.width,
    };
  };

  const commitTextBox = useCallback((box) => {
    const value = (box?.value || "").trim();
    if (value) {
      setOps((prev) => [...prev, { type: "text", color: box.color, x: box.x, y: box.y, value }]);
    }
    setTextBox(null);
  }, []);

  const handlePointerDown = (event) => {
    if (tool !== "draw") return;
    event.preventDefault();
    event.target.setPointerCapture?.(event.pointerId);
    const { x, y } = toCanvasCoords(event);
    strokeRef.current = { type: "stroke", color, points: [{ x, y }] };
  };
  const handlePointerMove = (event) => {
    if (!strokeRef.current) return;
    const { x, y } = toCanvasCoords(event);
    strokeRef.current.points.push({ x, y });
    // live preview: draw incrementally without committing
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const pts = strokeRef.current.points;
    ctx.strokeStyle = color;
    ctx.lineWidth = metrics.strokeWidth;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(pts[pts.length - 2].x, pts[pts.length - 2].y);
    ctx.lineTo(x, y);
    ctx.stroke();
  };
  const handlePointerUp = () => {
    const stroke = strokeRef.current;
    strokeRef.current = null;
    // drop accidental micro-strokes
    if (stroke && stroke.points.length >= 3) setOps((prev) => [...prev, stroke]);
    else redraw();
  };

  const handleCanvasClick = (event) => {
    if (tool !== "text") return;
    if (textBox) {
      commitTextBox(textBox);
      return;
    }
    const { x, y } = toCanvasCoords(event);
    setTextBox({ x, y, clientX: event.clientX, clientY: event.clientY, value: "", color });
  };

  // Escape layering: the text input cancels itself (stopPropagation), the next
  // Escape closes the editor - and never the report dialog beneath it.
  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key !== "Escape") return;
      event.stopPropagation();
      if (textBox) setTextBox(null);
      else onCancel();
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, [textBox, onCancel]);

  const handleSave = () => {
    if (textBox) commitTextBox(textBox);
    // commitTextBox updates state async; bake from current canvas after a frame
    requestAnimationFrame(() => {
      redraw();
      requestAnimationFrame(() => {
        const dataUrl = canvasRef.current.toDataURL("image/jpeg", 0.82);
        onSave(dataUrl);
      });
    });
  };

  // Backdrop click-retargeting guard: placing the text input mounts it UNDER the
  // cursor, so mouseup can land on the input and the browser fires click on the
  // backdrop (the common ancestor). Close only when pointer DOWN and click both
  // happened on the backdrop itself.
  const handleBackdropPointerDown = (event) => {
    backdropDownRef.current = event.target === event.currentTarget;
  };
  const handleBackdropClick = (event) => {
    if (event.target === event.currentTarget && backdropDownRef.current) onCancel();
    backdropDownRef.current = false;
  };

  const displayScale = (() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.width) return 1;
    const rect = canvas.getBoundingClientRect();
    return rect.width / canvas.width;
  })();

  return ReactDOM.createPortal(
    <div
      style={ui.backdrop}
      data-qa-exclude="true"
      onPointerDown={handleBackdropPointerDown}
      onClick={handleBackdropClick}
    >
      <div style={ui.toolbar} onPointerDown={(e) => e.stopPropagation()} onClick={(e) => e.stopPropagation()}>
        <button type="button" style={ui.toolBtn(tool === "draw")} onClick={() => setTool("draw")}>Draw</button>
        <button type="button" style={ui.toolBtn(tool === "text")} onClick={() => setTool("text")}>Text</button>
        <span style={{ width: "1px", height: "20px", background: "#E6E6E6" }} />
        {COLORS.map((swatch) => (
          <button
            key={swatch}
            type="button"
            aria-label={`color ${swatch}`}
            style={ui.swatch(swatch, color === swatch)}
            onClick={() => {
              setColor(swatch);
              if (textBox) setTextBox({ ...textBox, color: swatch });
            }}
          />
        ))}
        <span style={{ width: "1px", height: "20px", background: "#E6E6E6" }} />
        <button type="button" style={ui.toolBtn(false)} onClick={() => setOps((prev) => prev.slice(0, -1))} disabled={!ops.length}>Undo</button>
        <button type="button" style={ui.toolBtn(false)} onClick={() => setOps([])} disabled={!ops.length}>Clear</button>
        <button type="button" style={ui.toolBtn(false)} onClick={onCancel}>Cancel</button>
        <button
          type="button"
          style={{ ...ui.toolBtn(true), background: BRAND_CORAL, color: "#fff", border: `1px solid ${BRAND_CORAL}` }}
          onClick={handleSave}
        >
          Save
        </button>
      </div>
      <div style={ui.canvasWrap} onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
        <canvas
          ref={canvasRef}
          style={{
            maxWidth: "92vw",
            maxHeight: "74vh",
            borderRadius: "8px",
            touchAction: "none",
            cursor: tool === "draw" ? "crosshair" : "text",
            boxShadow: "0px 8px 30px rgba(0,0,0,0.4)",
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onClick={handleCanvasClick}
        />
      </div>
      {textBox && (
        <input
          autoFocus
          value={textBox.value}
          placeholder="Type label, Enter to place"
          onChange={(e) => setTextBox({ ...textBox, value: e.target.value })}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            // Escape cancels the box only; Enter commits. Both stop here.
            e.stopPropagation();
            if (e.key === "Enter") commitTextBox(textBox);
            if (e.key === "Escape") setTextBox(null);
          }}
          style={{
            position: "fixed",
            left: `${textBox.clientX}px`,
            top: `${textBox.clientY}px`,
            zIndex: Z_ANNOTATOR + 1,
            background: "rgba(30,41,57,0.45)",
            border: `1.5px dashed ${textBox.color}`,
            borderRadius: "4px",
            color: textBox.color,
            // WYSIWYG: on-screen size matches the baked fillText size
            fontSize: `${Math.max(11, metrics.fontPx * displayScale)}px`,
            fontWeight: 600,
            fontFamily: "Inter, sans-serif",
            padding: "2px 6px",
            outline: "none",
            minWidth: "120px",
          }}
        />
      )}
    </div>,
    document.body
  );
};

export default AnnotateShot;
